import {
  useRiskFactorsByCancerControllerGetUserRisk,
  useUserRiskControllerGetUserRisk,
} from '@/api/generated/default/default';
import { TypeOfCancer } from '@/components/icons/icon-catch/types';
import { RISK_LOWER_THRESHOLD, RISK_UPPER_THRESHOLD } from '@/constants/risks';
import { formatCancerTypeNameWithCapitalization } from '@/utils';
import { risksByCancerToSorted } from '@/utils/risks/sort-cancer-risks';
export enum RiskLevel {
  Above = 'above',
  Below = 'below',
  Average = 'average',
}

export default function useDashboardBodyController() {
  /**** Risks ****/

  const { data: riskData } = useUserRiskControllerGetUserRisk();

  const riskDataByCancer = riskData?.risk.risks ?? [];

  type ProcessedRiskData = Record<TypeOfCancer, { risk: number; level: RiskLevel }>;

  const processedRiskDataByCancer = riskDataByCancer.reduce((acc, { cancer, risk, averageRisk, potentialRisk }) => {
    acc[cancer] = {
      risk: risk,
      level: _getRiskLevel(risk, averageRisk),
    };
    return acc;
  }, {} as ProcessedRiskData);

  /*** Risk Factors ***/

  const { data: riskFactorsByCancer } = useRiskFactorsByCancerControllerGetUserRisk();
  const sortedRiskFactorsByCancer = risksByCancerToSorted(riskFactorsByCancer);

  const risks = sortedRiskFactorsByCancer?.factorsPerCancer.map(({ cancerType, riskFactorsSummary }) => ({
    cancerId: cancerType,
    name: formatCancerTypeNameWithCapitalization(cancerType),
    level: processedRiskDataByCancer?.[cancerType]?.level ?? RiskLevel.Average,
    risk:
      cancerType === 'overall' ? riskData?.risk.overallRisk ?? 0 : processedRiskDataByCancer?.[cancerType]?.risk ?? 0,
    /**
     * The positiveImpactRiskFactorsCount negatively affects the person,
     * represents a positive number risk and so, increases the overall risk.
     *
     * The negativeImpactRiskFactorsCount is beneficial for the person,
     * represents a negative number risk and so decreases the overall risk.
     */
    increasing: riskFactorsSummary.positiveImpactRiskFactorsCount,
    decreasing: riskFactorsSummary.negativeImpactRiskFactorsCount,
  }));

  const cancersCount = risks?.reduce<{ cancersAbove: number; cancersBelow: number; cancersAverage: number }>(
    (acc, { level }) => {
      if (level === RiskLevel.Above) {
        return { ...acc, cancersAbove: acc.cancersAbove + 1 };
      } else if (level === RiskLevel.Below) {
        return { ...acc, cancersBelow: acc.cancersBelow + 1 };
      } else {
        return { ...acc, cancersAverage: acc.cancersAverage + 1 };
      }
    },
    { cancersAbove: 0, cancersBelow: 0, cancersAverage: 0 }
  );

  /*** Step Size ***/

  const maxFactorsCount = Math.max(...(risks?.map(({ increasing, decreasing }) => increasing + decreasing) ?? []));

  risks?.sort((a, b) => a.cancerId.localeCompare(b.cancerId));

  const stepSize = 100 / maxFactorsCount;

  return {
    risks,
    cancersAbove: cancersCount?.cancersAbove ?? 0,
    cancersBelow: cancersCount?.cancersBelow ?? 0,
    cancersAverage: cancersCount?.cancersAverage ?? 0,
    stepSize,
  };
}

function _getRiskLevel(risk: number, averageRisk: number): RiskLevel {
  if (risk > averageRisk * RISK_UPPER_THRESHOLD) {
    return RiskLevel.Above;
  } else if (risk < averageRisk * RISK_LOWER_THRESHOLD) {
    return RiskLevel.Below;
  } else {
    return RiskLevel.Average;
  }
}
